import { CoreTag, CoreTooltip } from '@featuring-corp/components';
import { IconLaunchOutline, IconVerifiedBadge } from '@featuring-corp/icons';
import { vars } from '@styles/vanilla-extract/theme.css';
import { commonContainer, flex } from '@styles/vanilla-extract/utils';
import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react';

import { BiMap } from '@utils/objects';

import {
	bioClass,
	bioContainerClass,
	containerClass,
	fullNameClass,
	moreInfoClass,
	outLinkClass,
	platformIconClass,
	profileImgClass,
	rawDividerClass,
	rawKeyClass,
	rawValueClass,
	shrinkNoneClass,
	textContainerClass,
	textInnerContainer,
	tooltipClass,
	userNameClass,
	userNameContainerClass,
	userTextClass,
} from './style.css';
import { getAvatarColorType, getTagColorType } from './util';
import LargeAvatar from '../large-avatar/LargeAvatar';
import SnsRounded from '../sns-rounded/SnsRounded';
import { PlatformCode } from 'src/type/common';
import { Link } from 'react-router-dom';
import CustomLink from 'src/shared/custom-link/CustomLink';

export interface ReportProfileProps {
	profileImg?: string;
	platformCode?: PlatformCode;
	categories?: string[];
	pk?: string;
	username?: string;
	isVerified?: boolean;
	isTtcm?: boolean;
	fullName?: string;
	outLink?: string;
	bio?: string;
	rawData?: Record<string, ReactNode | undefined>;
}

const ReportProfile = ({
	profileImg,
	platformCode,
	categories = [],
	pk,
	username,
	isVerified,
	fullName,
	outLink,
	bio,
	rawData = {},
}: ReportProfileProps) => {
	const rawDataBiMap = new BiMap(rawData);

	const bioRef = useRef<HTMLParagraphElement>(null);
	const [bioOverFlown, setBioOverFlown] = useState(false);

	useEffect(() => {
		if (!bioRef.current) return;
		const bioElement = bioRef.current;
		const isOverFlown = bioElement.scrollWidth > bioElement.clientWidth;

		if (isOverFlown) {
			setBioOverFlown(true);
		} else if (!isOverFlown) {
			setBioOverFlown(false);
		}
	}, [bioRef, bio]);

	return (
		<div className={containerClass}>
			<div className={profileImgClass}>
				<LargeAvatar
					src={profileImg}
					alt={'influencer profile image'}
					name={username}
					colorType={getAvatarColorType(pk)}
					isBorder
				/>
				<SnsRounded platformCode={platformCode} className={platformIconClass} />
			</div>

			<div className={textContainerClass}>
				<div className={textInnerContainer}>
					<div className={flex({ gap: 'spacing-100' })}>
						{/* {platformCode === 'tw' && (
							<div className={betaPlatformContainerClass}>
								<div className={betaPlatformClass}>
									BETA{' '}
									{
										<CoreTooltip
											placement="bottom"
											autoAdjust
											// "엑스(X) 플랫폼은 현재 베타 버전으로 계정 분석 기능만 제한적으로 지원하고 있습니다. 추후 더 많은 기능이 업데이트될 예정입니다."
											text={t('infReportTwitter:profile.tag-tooltip')}
											zIndex={10}
											containerClassName={betaPlatformTooltip}
										>
											<IconInformationOutline size={'12px'} />
										</CoreTooltip>
									}
								</div>
							</div>
						)} */}
						{categories.map((category, idx) => (
							<CoreTag key={idx} tagType={getTagColorType(category)} text={category} />
						))}
					</div>

					<div className={userTextClass}>
						<div className={userNameContainerClass}>
							<h3 className={userNameClass}>{username}</h3>
							{isVerified && <IconVerifiedBadge className={shrinkNoneClass} size={14} />}
						</div>
						<p className={fullNameClass}>{fullName}</p>

						{outLink && (
							<div className={outLinkClass}>
								<CustomLink href={outLink} target="_blank" className={commonContainer}>
									<IconLaunchOutline fill={vars.semantic.color.icon.primary} />
								</CustomLink>
							</div>
						)}
						{!outLink && (
							<div className={outLinkClass} aria-disabled>
								<IconLaunchOutline />
							</div>
						)}
					</div>

					<div className={bioContainerClass}>
						<p className={bioClass} ref={bioRef}>
							{bio}
						</p>
						{bioOverFlown && (
							<div className={moreInfoClass}>
								<CoreTooltip
									text={<span className={tooltipClass}>{bio}</span>}
									placement="left-start"
									autoAdjust
									zIndex={10000}
								>
									<span>더보기</span>
								</CoreTooltip>
							</div>
						)}
					</div>
				</div>

				<div className={flex({ gap: 'spacing-400' })}>
					{rawDataBiMap.keys().map((key, idx) => (
						<Fragment key={idx}>
							{idx !== 0 && <div className={rawDividerClass} />}
							<div className={flex({ gap: 'spacing-100' })}>
								<span className={rawValueClass}>{rawDataBiMap.get(key)}</span>
								<span className={rawKeyClass}>{key}</span>
							</div>
						</Fragment>
					))}
				</div>
			</div>
		</div>
	);
};

export default ReportProfile;
